import { getApiErrorMessage } from '@/utils/errors';
import { isEmptyString } from '@/utils/string';
import type { CopilotSupportedMimeType } from '@customerio/hydra-copilot';
import type { useCopilotFileUpload } from '@customerio/hydra-copilot/react';

/**
 * Delays between retry attempts in milliseconds.
 * The number of entries determines the maximum number of retries.
 */
const UPLOAD_RETRY_DELAYS = [1000, 3000];

/**
 * Returns true if the error is a transient network failure worth retrying.
 * Server errors (4xx/5xx) are not retried since they won't succeed.
 */
function isRetryableUploadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message;
  return message === 'Network error during upload' || message === 'Upload was aborted';
}

type FileUploadHook = ReturnType<typeof useCopilotFileUpload>;

/**
 * Uploads all pending files with retry for transient network errors.
 * Retries individual files with exponential backoff (delays: 1s, 3s).
 */
export async function uploadFilesWithRetry(
  pendingFiles: FileUploadHook['files'],
  uploadFile: FileUploadHook['uploadFile'],
  removeFile: FileUploadHook['removeFile'],
): Promise<{
  successful: Array<{
    id: string;
    name: string;
    mimeType: CopilotSupportedMimeType;
    uploadedUrl: string;
  }>;
  failed: Array<{
    id: string;
    name: string;
    error: string;
  }>;
}> {
  if (pendingFiles.length === 0) {
    return { successful: [], failed: [] };
  }

  const results = await Promise.allSettled(
    pendingFiles.map(async (file) => {
      let lastError: unknown;

      for (let attempt = 0; attempt <= UPLOAD_RETRY_DELAYS.length; attempt++) {
        try {
          const uploadedUrl = await uploadFile(file.id);
          return {
            id: file.id,
            name: file.name,
            mimeType: file.mimeType as CopilotSupportedMimeType,
            uploadedUrl,
          };
        } catch (error) {
          lastError = error;

          if (attempt < UPLOAD_RETRY_DELAYS.length && isRetryableUploadError(error)) {
            await new Promise((resolve) => setTimeout(resolve, UPLOAD_RETRY_DELAYS[attempt]));
            continue;
          }

          break;
        }
      }

      throw lastError;
    }),
  );

  const successful: Array<{
    id: string;
    name: string;
    mimeType: CopilotSupportedMimeType;
    uploadedUrl: string;
  }> = [];
  const failed: Array<{
    id: string;
    name: string;
    error: string;
  }> = [];

  for (const [index, result] of results.entries()) {
    const file = pendingFiles[index];
    if (result.status === 'fulfilled') {
      successful.push(result.value);
    } else {
      const errorMessage = getApiErrorMessage(result.reason, 'Upload failed');

      // If the file was removed by the user during a retry delay, skip it
      // silently — don't report as a failure since removal was intentional.
      if (errorMessage.includes('File with ID') && errorMessage.includes('not found')) {
        continue;
      }

      failed.push({ id: file.id, name: file.name, error: errorMessage });
      removeFile(file.id);
    }
  }

  return { successful, failed };
}

/**
 * Extracts the filename from a file reference path (e.g., gs://bucket/path/file.png -> file.png).
 * Handles both forward and backward slashes.
 */
export function extractFilename(fileReference: string): string {
  const pathParts = fileReference.split(/[/\\]/);
  return !isEmptyString(pathParts[pathParts.length - 1]) ? pathParts[pathParts.length - 1] : fileReference;
}

/**
 * Fetches a URL and converts it to a blob URL for caching.
 * Used to cache files locally when server sends cache-control: max-age=0
 */
export async function fetchAsBlob(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.statusText}`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
